import type { AppShell } from "@/App"
import { RequestError, toRequestError } from "./request-error"
import type { SerialActivationAsk } from "./SerialActivationDialog"

/**
 * Cliente da Ronda de stock (0.32.0). As funções vivem em
 * `functions/api/armazem/rondas/**` (área Armazém). Uma ronda conta uma
 * localização e, no fim, **aplica** no Odoo: quem lê aprova no «Rever».
 */

export type RondaStatus =
  | "aberta"
  | "por_rever"
  | "aplicada"
  | "aplicada_parcial"
  | "fechada_com_pendentes"
  | "abandonada"
  | "enviada"
  | "parcialmente_aplicada"
  | "cancelada"
export type RondaMode = "parcial" | "completa"
export type ProductState = "por_enviar" | "aplicado" | "por_recontar" | "por_resolver"

export interface LastSend {
  at: string
  by_name: string | null
  assigned: number
  applied: number
  unchanged: number
  outstanding: number
  warnings?: string[]
}

export interface RondaListItem {
  id: string
  status: RondaStatus
  location_name: string | null
  van_id: string | null
  mode: RondaMode
  blind: boolean
  source: "armazem" | "campo"
  created_by_name: string | null
  in_review: boolean
  products: number | null
  to_recount: number
  to_resolve: number
  last_send: LastSend | null
  sending?: boolean
  sent_at: string | null
  updated_at: string
}

export interface RondaLocation {
  key: string
  label: string
  van_id: string | null
  location: string | number | null
  shelf: boolean
}

export interface PlanLine {
  product_id: number
  lot_name: string | null
  counted_qty: number
  expected_qty: number
  reason: "contado" | "serie_a_mais" | "serie_em_falta" | "resto_sem_serie" | "vai_a_zero"
  /** Por resolver por causa das reservas para entregas (aplica só até ao reservado), ou unidades sem série não lidas. */
  hold?: string | null
  hold_status?: string
  /** `false`: não vai ao Odoo. */
  send?: boolean
  /** 0.37.0 — vai ao host: substituir esta contagem pendente de outra pessoa. */
  replace_pending?: { user_id: number | null; inventory_quantity: number } | null
}

/** Uma contagem pendente no Odoo (de quem, quanto, quando — `date` ISO). */
export interface PendingInfo {
  lot_name: string | null
  user_id: number | null
  user_name: string | null
  inventory_quantity: number
  date: string | null
}

export type ReviewSection = "igual" | "series" | "diferenca" | "zero" | "por_resolver"

export interface ReviewItem {
  product_id: number
  product_name: string | null
  default_code: string | null
  uom: string | null
  tracking: string | null
  section: ReviewSection
  odoo_qty: number
  counted_qty: number
  reserved?: number
  sub_stock?: Array<{ location_name: string | null; quantity: number }>
  odoo_no_lot?: number
  odoo_serials?: string[]
  read_serials?: string[]
  assign: string[]
  extra?: Array<{ lot_name: string; warning: string | null }>
  missing?: string[]
  elsewhere?: Array<{ lot_name: string; location_name: string | null }>
  lines: PlanLine[]
  pending_by?: string | null
  /** Contagens pendentes no Odoo neste artigo, aqui. */
  pending?: PendingInfo[]
  /** «Substituir pela minha contagem» possível (artigo sem rastreio, uma só pendente). */
  can_replace_pending?: boolean
  /** Confirmado: a contagem pendente desta pessoa é substituída no envio. */
  replace_pending?: { user_id: number | null; user_name: string | null; inventory_quantity: number; date: string | null } | null
  /** `hint`: o que fazer, em texto secundário. */
  blocked?: { status: string; message: string; hint?: string | null } | null
}

export interface Review {
  at: string
  by_name: string | null
  note: string
  items: ReviewItem[]
  lot_tracked_skipped: number
}

export interface LineResult {
  status: string
  message?: string | null
  quantity_before?: number | null
  quantity_after?: number | null
  lot_created?: boolean
  /** A contagem pendente de outra pessoa que o envio substituiu. */
  replaced_pending?: { user_name: string | null; inventory_quantity: number } | null
  /** `pending_other`: a contagem pendente que está agora no Odoo. */
  pending?: PendingInfo[]
  send_no?: number
  at?: string
}

export interface Ronda {
  id: string
  location_id: number
  location_name: string | null
  van_id: string | null
  mode: RondaMode
  blind: boolean
  source: "armazem" | "campo"
  status: RondaStatus
  note: string | null
  counting_closed_at: string | null
  created_by_name: string | null
  sent_at: string | null
  result_json: {
    review?: Review | null
    sending?: unknown
    sends?: number
    last_send?: LastSend
    recount?: number[]
    closed?: { at: string; by_name: string | null; reason: string; pending: Array<{ product_id: number; product_name: string | null; lot_name: string | null; status: string; message: string | null }> } | null
  } | null
  version: number
  created_at: string
}

export interface RondaLine {
  id: string
  product_id: number
  product_name: string | null
  default_code: string | null
  tracking: string | null
  uom: string | null
  lot_name: string | null
  counted_qty: number | string
  expected_qty_at_count: number | string | null
  source: string | null
  warning: string | null
  counted_by_name: string | null
  counted_at: string
  send_result: LineResult | null
}

export interface RondaProduct {
  product_id: number
  product_name: string | null
  default_code: string | null
  uom: string | null
  tracking: string | null
  counted_qty: number
  odoo_qty: number | null
  serials: Array<{ line_id: string; lot_name: string; warning: string | null; counted_by_name: string | null; counted_at: string }>
  line_id: string | null
  counted_by_name: string | null
  counted_at: string
  state: ProductState
}

export interface RondaDetail {
  check: Ronda
  lines: RondaLine[]
  products: RondaProduct[]
  hidden_odoo: boolean
  is_admin: boolean
  can_manage: boolean
  legacy: boolean
  legacy_note: string | null
}

export interface PickProduct {
  id: number
  name: string | null
  default_code: string | null
  barcode: string | null
  tracking: string | null
  uom?: string | null
  /** `false`: sem controlo de stock no Odoo («Rastrear inventário» desligado) — não se conta. */
  storable?: boolean
  /** O nome no Odoo sem a referência à frente (só nos `storable: false`): vai em `expected` ao substituir o artigo. */
  odoo_name?: string | null
  /** Fora da contagem cega: o que o Odoo tem nesta localização (solto) e quanto está reservado. */
  odoo_qty?: number
  odoo_reserved?: number
}

export type ScanResult =
  | "somado"
  | "serie"
  | "ja_lida"
  | "artigo_com_serie"
  | "codigo_produto"
  | "outro_artigo"
  | "parece_ean"
  | "desconhecido"
  | "ambiguo"
  | "nao_armazenavel"
  | "por_receber"
  | "ja_acertado"
  /** «Ler séries» num artigo sem rastreio: o aviso para ativar o número de série (nada escrito). */
  | "ativar_serie"
  /** «É o EAN / o código deste artigo» a ler séries (`count: false`): código gravado no Odoo, nada contado. */
  | "associado"

/** Quem ativou o número de série e quando (ledger da ronda / da receção). */
export interface SerialActivationRecord {
  product_id: number
  product_name: string | null
  serial: string | null
  by: string | null
  by_name: string | null
  at: string
  already: boolean
  removed_qty?: number | null
}

export interface ScanOutcome {
  result: ScanResult
  message: string
  code: string
  product?: { id: number; name: string | null; default_code: string | null; tracking: string; uom: string | null } | null
  line?: RondaLine | null
  can_associate?: boolean
  /** O código a associar não é um EAN/GTIN: só com o aviso confirmado (`allow_non_gtin`). */
  non_gtin?: boolean
  activation?: SerialActivationAsk | null
  activated?: SerialActivationRecord | null
  /** Série contada (ou já lida) num artigo com séries que o Odoo não conhece de todo: «Não é série — Guardar como código de barras». */
  maybe_product_code?: boolean
  /** Como o código casou (`supplier_code` = código do fornecedor). */
  match?: string | null
  supplier?: string | null
}

type Res<T> = { success: true; data: T } | { success: false; error: string; details?: unknown }

async function guard<T>(p: Promise<Res<T>>): Promise<T> {
  try {
    const res = await p
    if (!res.success) throw new RequestError(res.error || "Pedido falhou", 400, res.details)
    return res.data
  } catch (e) {
    throw toRequestError(e)
  }
}

export const listRondas = (shell: AppShell) => guard(shell.client.get<Res<{ items: RondaListItem[]; can_manage: boolean }>>("armazem/rondas"))

export const listRondaLocations = (shell: AppShell) => guard(shell.client.get<Res<{ items: RondaLocation[]; can_manage: boolean }>>("armazem/rondas/locations"))

export const createRonda = (shell: AppShell, body: { van_id?: string | null; location?: string | number | null; mode: RondaMode; blind: boolean }) =>
  guard(shell.client.post<Res<Ronda>>("armazem/rondas", body))

export const getRonda = (shell: AppShell, id: string) => guard(shell.client.get<Res<RondaDetail>>(`armazem/rondas/${id}`))

export const patchRonda = (
  shell: AppShell,
  id: string,
  body: {
    set_qty?: { product_id: number; qty: number }
    remove_line?: string
    close_counting?: true
    reopen_counting?: true
    recount_product?: number
    abandon?: true
    discard_sending?: true
    close_with_pending?: true
    reason?: string
  },
) => guard(shell.client.patch<Res<RondaDetail>>(`armazem/rondas/${id}`, body))

export const scanRonda = (shell: AppShell, id: string, body: { code: string; product_id?: number | null; force_ean?: boolean; as_serial?: boolean }) =>
  guard(shell.client.post<Res<ScanOutcome>>(`armazem/rondas/${id}/scans`, body))

/** «Ativar e guardar a série» (0.35.0): liga o número de série no Odoo e conta a série. */
export const activateRondaSerial = (shell: AppShell, id: string, body: { product_id: number; code: string; force_ean?: boolean }) =>
  guard(shell.client.post<Res<ScanOutcome>>(`armazem/rondas/${id}/activate-serial`, body))

/** `count: false` (a ler séries): só grava o código no artigo, sem contar (`associado`). */
export const associateCode = (shell: AppShell, id: string, body: { product_id: number; code: string; allow_non_gtin?: boolean; count?: boolean }) =>
  guard(shell.client.post<Res<ScanOutcome>>(`armazem/rondas/${id}/associate`, body))

/** O que «Criar artigo com stock (arquiva este)» devolve (0.44.0). */
export interface ReplaceProductOutcome {
  replacement: { old_product_id: number; new_product_id: number; default_code: string | null; crm_repointed: boolean; warnings: string[]; replayed?: boolean }
  product: { id: number; name: string | null; default_code: string | null; barcode: string | null; tracking: string; uom: string | null; storable: true; odoo_qty?: number; odoo_reserved?: number }
  warnings: string[]
  repointed: Record<string, number>
}

/** «Criar artigo com stock (arquiva este)»: arquiva o artigo sem controlo de stock e cria um novo com stock no Odoo. */
export const replaceRondaProduct = (shell: AppShell, id: string, body: { product_id: number; expected: { default_code: string | null; name: string | null } }) =>
  guard(shell.client.post<Res<ReplaceProductOutcome>>(`armazem/rondas/${id}/replace-product`, body))

export const searchRondaProducts = (shell: AppShell, id: string, q: string, all: boolean) =>
  guard(shell.client.get<Res<{ items: PickProduct[] }>>(`armazem/rondas/${id}/products?q=${encodeURIComponent(q)}${all ? "&stock=all" : ""}`)).then((d) => d.items)

export const reviewRonda = (shell: AppShell, id: string) => guard(shell.client.post<Res<{ review: Review; can_send: boolean }>>(`armazem/rondas/${id}/review`, {}))

/**
 * «Substituir pela minha contagem» (0.37.0): confirma que a contagem pendente
 * (exatamente esta) se descarta; devolve o «Rever» refeito. 409 se mudou.
 */
export const confirmReplacePending = (shell: AppShell, id: string, body: { product_id: number; user_id: number | null; inventory_quantity: number }) =>
  guard(shell.client.post<Res<{ review: Review; can_send: boolean }>>(`armazem/rondas/${id}/review`, { replace_pending: body }))

export const sendRonda = (shell: AppShell, id: string, reviewAt: string) => guard(shell.client.post<Res<RondaDetail>>(`armazem/rondas/${id}/send`, { review_at: reviewAt }))

export const RONDA_STATUS: Record<RondaStatus, { label: string; variant: "info" | "success" | "neutral" | "warning" }> = {
  aberta: { label: "A contar", variant: "info" },
  por_rever: { label: "Por rever", variant: "warning" },
  aplicada: { label: "Aplicada", variant: "success" },
  aplicada_parcial: { label: "Aplicada com linhas por recontar", variant: "warning" },
  fechada_com_pendentes: { label: "Fechada com pendentes", variant: "neutral" },
  abandonada: { label: "Abandonada", variant: "neutral" },
  enviada: { label: "Conferência antiga · pendentes no Odoo", variant: "neutral" },
  parcialmente_aplicada: { label: "Conferência antiga · pendentes no Odoo", variant: "neutral" },
  cancelada: { label: "Conferência antiga · envio cancelado", variant: "neutral" },
}

/** O resultado de uma linha no envio, para o ecrã. Um estado que não se conheça é «por resolver». */
export const LINE_RESULT: Record<string, { label: string; cls: string }> = {
  applied: { label: "Acertado", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  assigned: { label: "Série atribuída", cls: "bg-violet-50 text-violet-800 border-violet-200" },
  unchanged: { label: "Igual", cls: "bg-muted text-muted-foreground border-border" },
  changed: { label: "Por recontar", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  reservado: { label: "Reservado para entregas", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  reserved: { label: "Reservado para entregas", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  sem_etiqueta: { label: "Sem série lida", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  sublocalizacao: { label: "Também numa prateleira", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  pending_other: { label: "Contagem pendente no Odoo", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  packaged: { label: "Em embalagens", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  elsewhere: { label: "Noutra localização", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  tracked_without_lot: { label: "Precisa de séries", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  not_storable: { label: "Não armazenável", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  invalid: { label: "Por resolver", cls: "bg-red-50 text-red-800 border-red-200" },
  error: { label: "Erro", cls: "bg-red-50 text-red-800 border-red-200" },
}
export const lineResultLabel = (status: string) => LINE_RESULT[status] ?? { label: "Por resolver", cls: "bg-red-50 text-red-800 border-red-200" }

export const num = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
export const fmtQty = (v: number) => (Number.isInteger(v) ? String(v) : v.toLocaleString("pt-PT", { maximumFractionDigits: 3 }))
export const signed = (v: number) => (v > 0 ? `+${fmtQty(v)}` : fmtQty(v))

// ─── Contagens pendentes de outras pessoas (0.37.0) ─────────────────────────

/** «un.» para as unidades do Odoo («Units»); o resto como vem («m»). */
export const unitLabel = (uom: string | null | undefined) => (!uom || /^(units?|un\.?|unidades?|unit[eé]s?)$/i.test(uom.trim()) ? "un." : uom)

const dayOf = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", timeZone: "Europe/Lisbon" }) : null)

/** Um aviso no cartão do «Rever». */
export interface ReviewNotice {
  tone: "red" | "amber"
  text: string
  /** Texto secundário (o que fazer no Odoo). */
  hint?: string | null
  /** Série a que o aviso se refere (reservas). */
  lot_name?: string | null
}

/**
 * Os avisos de um artigo no «Rever», por ordem — **um só** para uma contagem
 * pendente (o motivo do servidor, com quem/quanto/quando; o caminho no Odoo em
 * texto secundário). Antes havia dois quase iguais (o vermelho do bloqueio e
 * um âmbar «o envio pode recusar a linha»).
 */
export function reviewNotices(item: ReviewItem): ReviewNotice[] {
  const out: ReviewNotice[] = []
  for (const l of item.lines) if (l.hold && item.section !== "por_resolver") out.push({ tone: "amber", text: l.hold, lot_name: l.lot_name })
  if (item.blocked) out.push({ tone: "red", text: item.blocked.message, hint: item.blocked.hint ?? null })
  const r = item.replace_pending
  if (r) {
    const what = `${r.user_name ?? "outra pessoa"} (${fmtQty(r.inventory_quantity)} ${unitLabel(item.uom)})`
    out.push({
      tone: "amber",
      text: item.lines.some((l) => l.replace_pending)
        ? `Ao enviar, a contagem pendente de ${what} é descartada no Odoo.`
        : `Contado igual ao Odoo: nada se escreve e a contagem pendente de ${what} fica no Odoo.`,
    })
  }
  return out
}

/** O texto da confirmação de «Substituir pela minha contagem». */
export function replaceConfirmText(item: ReviewItem): string {
  const p = item.pending?.[0]
  const unit = unitLabel(item.uom)
  const who = p?.user_name ?? "outra pessoa"
  const when = dayOf(p?.date ?? null)
  return `A contagem pendente de ${who} (${fmtQty(p?.inventory_quantity ?? 0)} ${unit}${when ? `, ${when}` : ""}) é descartada e o stock passa a ${fmtQty(item.counted_qty)} ${unit}${unit.endsWith(".") ? "" : "."} Continuar?`
}
